import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Search, Filter, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, User,
  Activity, MapPin, Monitor, Building, Users, X
} from 'lucide-react';
import api from '../../utils/api';

const MobileSecurityLogs = () => {
  const [selectedTab, setSelectedTab] = useState('security');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [logsPerPage] = useState(20);

  const [securityEvents, setSecurityEvents] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [securityPage, setSecurityPage] = useState(1);
  const [securityTotalEvents, setSecurityTotalEvents] = useState(0);
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [eventUserTypeFilter, setEventUserTypeFilter] = useState('all');

  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessPage, setAccessPage] = useState(1);
  const [accessTotalLogs, setAccessTotalLogs] = useState(0);
  const [logStatusFilter, setLogStatusFilter] = useState('all');
  const [portalFilter, setPortalFilter] = useState('all');

  useEffect(() => {
    fetchSecurityEvents();
  }, [securityPage, eventStatusFilter, severityFilter, eventUserTypeFilter, searchQuery]);

  useEffect(() => {
    fetchAccessLogs();
  }, [accessPage, logStatusFilter, portalFilter, searchQuery]);

useEffect(() => {
  setShowFilters(false);
}, [selectedTab]);

  const fetchSecurityEvents = async () => {
    try {
      setSecurityLoading(true);
      const params = new URLSearchParams({
        page: securityPage,
        limit: logsPerPage,
        search: searchQuery,
        status: eventStatusFilter !== 'all' ? eventStatusFilter : '',
        severity: severityFilter !== 'all' ? severityFilter : '',
        user_type: eventUserTypeFilter !== 'all' ? eventUserTypeFilter : ''
      });

      const response = await api.get(`/api/security/events?${params}`);

      if (response.data.events) {
        setSecurityEvents(response.data.events);
        setSecurityTotalEvents(response.data.total || response.data.events.length || 0);
      }
    } catch (error) {
      console.error('Error fetching security events:', error);
      setSecurityEvents([]);
    } finally {
      setSecurityLoading(false);
    }
  };

  const fetchAccessLogs = async () => {
    try {
      setAccessLoading(true);
      const params = new URLSearchParams({
        page: accessPage,
        limit: logsPerPage,
        search: searchQuery,
        status: logStatusFilter !== 'all' ? logStatusFilter : '',
        user_type: portalFilter !== 'all' ? portalFilter : ''
      });

      const response = await api.get(`/api/security/access-logs?${params}`);
      
      if (response.data.accessLogs) {
        setAccessLogs(response.data.accessLogs);
        setAccessTotalLogs(response.data.total || response.data.accessLogs.length || 0);
      }
    } catch (error) {
      console.error('Error fetching access logs:', error);
      setAccessLogs([]);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleSearch = () => {
    setSecurityPage(1);
    setAccessPage(1);
    fetchSecurityEvents();
    fetchAccessLogs();
  };
  const handleRefresh = () => {
    if (selectedTab === 'security') {
      fetchSecurityEvents();
    } else {
      fetchAccessLogs();
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return '1d ago';
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPortalBadge = (portalType) => {
    const isClientPortal = portalType === 'Client Portal' || portalType === 'client';
    return isClientPortal
      ? { color: 'bg-blue-100 text-blue-800', icon: Building, text: 'Client Portal' }
      : { color: 'bg-purple-100 text-purple-800', icon: Users, text: 'Staff Portal' };
  };

  const getSeverityBadge = (severity) => {
    const map = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-amber-100 text-amber-800',
      low: 'bg-green-100 text-green-800'
    };
    const label = severity ? severity.toUpperCase() : 'UNKNOWN';
    return { color: map[severity] || 'bg-gray-100 text-gray-700', label };
  };

  const getEventStatusBadge = (status) => {
    const normalized = String(status || 'pending').toLowerCase();
    const map = {
      pending: 'bg-yellow-100 text-yellow-800',
      investigating: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      false_positive: 'bg-gray-200 text-gray-700',
      active: 'bg-red-100 text-red-800'
    };
    return {
      color: map[normalized] || 'bg-gray-100 text-gray-700',
      label: normalized.replace('_', ' ').toUpperCase()
    };
  };

  const getStatusColor = (status) => {
    return status === 'Success' || status === 'success'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const getStatusIcon = (status) => {
    return status === 'Success' || status === 'success'
      ? <CheckCircle className="w-4 h-4" />
      : <XCircle className="w-4 h-4" />;
  };

  const filteredAccessLogs = accessLogs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.user_name?.toLowerCase().includes(query) ||
      log.email?.toLowerCase().includes(query) ||
      log.ip_address?.toLowerCase().includes(query)
    );
  });

  const securityTotalPages = Math.max(1, Math.ceil(securityTotalEvents / logsPerPage));
  const accessTotalPages = Math.max(1, Math.ceil(accessTotalLogs / logsPerPage));

  const filtersActive = selectedTab === 'security'
    ? (severityFilter !== 'all' || eventStatusFilter !== 'all' || eventUserTypeFilter !== 'all')
    : (logStatusFilter !== 'all' || portalFilter !== 'all');

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Security & Access Logs</h1>
              <p className="text-sm text-gray-600 mt-1">Monitor system access</p>
            </div>
            <button
              onClick={handleRefresh}
              className="p-2 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
            <button
              onClick={() => setSelectedTab('security')}
              className={`py-2 rounded-xl text-sm font-semibold transition-colors ${
                selectedTab === 'security'
                  ? 'bg-white text-[#1F11ED] shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Security Events
            </button>
            <button
              onClick={() => setSelectedTab('access')}
              className={`py-2 rounded-xl text-sm font-semibold transition-colors ${
                selectedTab === 'access'
                  ? 'bg-white text-[#1F11ED] shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Access Logs
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters || filtersActive
                  ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              {selectedTab === 'security' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                    <select
                      value={severityFilter}
                      onChange={(e) => setSeverityFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                    >
                      <option value="all">All severities</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={eventStatusFilter}
                      onChange={(e) => setEventStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                    >
                      <option value="all">All statuses</option>
                      <option value="pending">Pending</option>
                      <option value="investigating">Investigating</option>
                      <option value="resolved">Resolved</option>
                      <option value="false_positive">False Positive</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                    <select
                      value={eventUserTypeFilter}
                      onChange={(e) => setEventUserTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                    >
                      <option value="all">All users</option>
                      <option value="client">Client</option>
                      <option value="staff">Staff</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Portal Type</label>
                    <select
                      value={portalFilter}
                      onChange={(e) => setPortalFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                    >
                      <option value="all">All portals</option>
                      <option value="client">Client Portal</option>
                      <option value="staff">Staff Portal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={logStatusFilter}
                      onChange={(e) => setLogStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                    >
                      <option value="all">All status</option>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </>
              )}
              <button
                onClick={() => {
                  if (selectedTab === 'security') {
                    setSeverityFilter('all');
                    setEventStatusFilter('all');
                    setEventUserTypeFilter('all');
                  } else {
                    setPortalFilter('all');
                    setLogStatusFilter('all');
                  }
                  setSearchQuery('');
                  setShowFilters(false);
                }}
                className="w-full text-sm text-gray-600 py-2"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Security Events */}
      {selectedTab === 'security' && (
        <>
          <div className="p-4 space-y-3">
            {securityLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : securityEvents.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No security events</h3>
                <p className="text-sm text-gray-600">
                  {searchQuery || eventStatusFilter !== 'all' || severityFilter !== 'all' || eventUserTypeFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'No security events recorded yet'}
                </p>
              </div>
            ) : (
              <AnimatePresence>
                {securityEvents.map((event, index) => {
                  const severityBadge = getSeverityBadge(event.severity);
                  const statusBadge = getEventStatusBadge(event.status);
                  const portalInfo = getPortalBadge(event.userType || event.user_type);
                  const PortalIcon = portalInfo.icon;

                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${severityBadge.color}`}>
                              {severityBadge.label}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${statusBadge.color}`}>
                              {statusBadge.label}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(event.createdAt || event.created_at || event.timestamp)}</span>
                        </div>

                        <h3 className="font-semibold text-gray-900 text-base">
                          {event.description || event.eventType || event.event_type || 'Security event'}
                        </h3>

                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${portalInfo.color}`}>
                            <PortalIcon className="w-3 h-3" />
                            <span>{portalInfo.text}</span>
                          </span>
                          {event.source?.service && (
                            <span className="text-xs text-gray-500 uppercase">{event.source.service}</span>
                          )}
                        </div>

                        {event.user?.email && (
                          <p className="text-sm text-gray-600 flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>{event.user.email}</span>
                          </p>
                        )}

                        {event.ipAddress && (
                          <p className="text-sm text-gray-600 flex items-center space-x-2">
                            <MapPin className="w-4 h-4" />
                            <span>{event.ipAddress}</span>
                          </p>
                        )}

                        {event.location && event.location !== 'Unknown' && (
                          <p className="text-sm text-gray-600">{event.location}</p>
                        )}

                        {event.notes && (
                          <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                            {event.notes}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>

          {securityTotalPages > 1 && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setSecurityPage(prev => Math.max(1, prev - 1))}
                    disabled={securityPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      securityPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                    }`}
                  >
                    Previous
                  </button>
                  
                  <span className="text-sm text-gray-600">
                    Page {securityPage} of {securityTotalPages}
                  </span>
                  
                  <button
                    onClick={() => setSecurityPage(prev => Math.min(securityTotalPages, prev + 1))}
                    disabled={securityPage === securityTotalPages}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      securityPage === securityTotalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="text-center mt-2 text-xs text-gray-500">
                  {securityTotalEvents} total security events
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Access Log List */}
      {selectedTab === 'access' && (
        <>
          <div className="p-4 space-y-3">
            {accessLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : filteredAccessLogs.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No access logs found</h3>
                <p className="text-sm text-gray-600">
                  {searchQuery || logStatusFilter !== 'all' || portalFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'No access logs yet'}
                </p>
              </div>
            ) : (
              <AnimatePresence>
                {filteredAccessLogs.map((log, index) => {
                  const portalBadge = getPortalBadge(log.portal_type || log.portalType);
                  const PortalIcon = portalBadge.icon;
                  
                  return (
                    <motion.div
                      key={log.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${portalBadge.color}`}>
                                <PortalIcon className="w-3 h-3" />
                                <span>{portalBadge.text}</span>
                              </span>
                              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                                {getStatusIcon(log.status)}
                                <span>{log.status}</span>
                              </span>
                            </div>
                            <h3 className="font-semibold text-gray-900 text-base mb-1">
                              {log.user_name || log.userName || 'Unknown User'}
                            </h3>
                            <p className="text-sm text-gray-600">{log.email}</p>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            {formatDate(log.timestamp)}
                          </div>
                        </div>

                        {/* Log Details */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Activity className="w-4 h-4" />
                            <span className="font-medium">Action:</span>
                            <span>{log.action || 'Login'}</span>
                          </div>
                          {log.ip_address && (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span className="font-medium">IP:</span>
                              <span className="break-all">{log.ip_address}</span>
                            </div>
                          )}
                          {log.location && log.location !== 'Unknown' && (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span className="font-medium">Location:</span>
                              <span>{log.location}</span>
                            </div>
                          )}
                          {(log.device || log.browser) && (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <Monitor className="w-4 h-4" />
                              <span className="font-medium">Device:</span>
                              <span>{[log.device, log.browser].filter(Boolean).join(' - ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>

          {/* Pagination */}
          {accessTotalPages > 1 && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setAccessPage(prev => Math.max(1, prev - 1))}
                    disabled={accessPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      accessPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                    }`}
                  >
                    Previous
                  </button>
                  
                  <span className="text-sm text-gray-600">
                    Page {accessPage} of {accessTotalPages}
                  </span>
                  
                  <button
                    onClick={() => setAccessPage(prev => Math.min(accessTotalPages, prev + 1))}
                    disabled={accessPage === accessTotalPages}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      accessPage === accessTotalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="text-center mt-2 text-xs text-gray-500">
                  {accessTotalLogs} total access logs
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MobileSecurityLogs;
